import {useMutation, useQuery, useQueryClient} from '@tanstack/react-query';

import {
  RQ_CANCEL_BATCH_API,
  RQ_CREATE_BATCH_API,
  RQ_DELETE_PRODUCTION_API,
  RQ_GET_ALL_PRODUCTION_API,
  RQ_GET_AVAILABLE_BATCHES_API,
  RQ_PRODUCTION_BY_IDS_API,
  RQ_PRODUCTS_BY_FILTER_API,
  RQ_PUT_PRODUCTION_API,
  RQ_UPDATE_PRODUCTION_API,
} from './constants';
import {rqCreateRequest} from './rqHttpUtils';

export const RqGetAllProductions = (token, options = {}) => {
  return useQuery({
    queryKey: ['rq_get_all_productions'],
    queryFn: async () => {
      const response = await rqCreateRequest(RQ_GET_ALL_PRODUCTION_API, null, token);
      return response || [];
    },
    refetchOnWindowFocus: false,
    retry: 1,
    ...options,
  });
};

/* --- STAGE 1 BATCH PRODUCTION HOOKS --- */
export const RqGetAvailableBatches = (token, options = {}) => {
  return useQuery({
    queryKey: ['rq_get_available_batches'],
    queryFn: async () => {
      const response = await rqCreateRequest(RQ_GET_AVAILABLE_BATCHES_API, null, token);
      return response || [];
    },
    enabled: !!token,
    refetchOnWindowFocus: false,
    retry: 1,
    ...options,
  });
};

export const RqCreateBatch = (options = {}) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ['rq_create_batch'],
    mutationFn: async ({payload, token}) => {
      if (!token) {throw new Error('Auth token missing');}
      return rqCreateRequest(RQ_CREATE_BATCH_API, payload, token);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({queryKey: ['rq_get_available_batches']});
      queryClient.invalidateQueries({queryKey: ['rq_get_all_products']});
      options?.onSuccess?.();
    },
    ...options,
  });
};

export const RqCancelBatch = (options = {}) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ['rq_cancel_batch'],
    mutationFn: async ({id, token}) => {
      if (!token) {throw new Error('Auth token missing');}
      return rqCreateRequest(RQ_CANCEL_BATCH_API, null, token, {id});
    },
    onSuccess: () => {
      queryClient.invalidateQueries({queryKey: ['rq_get_available_batches']});
      queryClient.invalidateQueries({queryKey: ['rq_get_all_products']});
      options?.onSuccess?.();
    },
    ...options,
  });
};

export const RqGetProductionById = (id, token, options = {}) => {
  return useQuery({
    queryKey: ['rq_get_production', id],
    queryFn: async () => {
      return rqCreateRequest(RQ_PRODUCTION_BY_IDS_API, null, token, {id});
    },
    enabled: !!id && !!token,
    refetchOnWindowFocus: false,
    retry: 1,
    ...options,
  });
};

export const RqGetProductionsByFilter = (filters, token, options = {}) => {
  return useQuery({
    queryKey: ['rq_get_productions_by_filter', filters],
    queryFn: async ({queryKey, signal}) => {
      const filtersData = queryKey[1];
      if (!filtersData || Object.keys(filtersData).length === 0) {return [];}

      return rqCreateRequest(RQ_PRODUCTS_BY_FILTER_API, filtersData, token, null, signal);
    },
    enabled: Object.keys(filters || {}).length > 0,
    keepPreviousData: true,
    ...options,
  });
};

export const RqRegisterProduction = (options = {}) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ['rq_register_production'],
    mutationFn: async ({payload, token}) => {
      if (!token) {throw new Error('Auth token missing');}
      return rqCreateRequest(RQ_PUT_PRODUCTION_API, payload, token);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({queryKey: ['rq_get_all_productions']});
      queryClient.invalidateQueries({queryKey: ['rq_get_available_batches']});
      queryClient.invalidateQueries({queryKey: ['rq_get_all_products']});
      queryClient.invalidateQueries({queryKey: ['rq_get_all_final_products']});
      options?.onSuccess?.();
    },
    ...options,
  });
};

export const RqUpdateProduction = (options = {}) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ['rq_update_production_allfields'],
    mutationFn: async ({id, payload, token}) => {
      if (!token) {throw new Error('Auth token missing');}
      return rqCreateRequest(RQ_UPDATE_PRODUCTION_API, payload, token, {id});
    },
    onSuccess: (data, variables) => {
      const {id} = variables;
      queryClient.invalidateQueries({queryKey: ['rq_get_production', id]});
      queryClient.invalidateQueries({queryKey: ['rq_get_all_productions']});
    },
    ...options,
  });
};

export const RqDeleteProduction = (options = {}) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ['rq_delete_production'],
    mutationFn: async ({id, token}) => {
      if (!token) {throw new Error('Auth token missing');}
      return rqCreateRequest(RQ_DELETE_PRODUCTION_API, null, token, {id});
    },
    onSuccess: () => {
      queryClient.invalidateQueries({queryKey: ['rq_get_all_productions']});
    },
    ...options,
  });
};
